import { CELLS, EDGE, MINE_COUNTS, minesMultiplier, SIZE, type MinesState } from '../../engine/games/mines';
import { money } from '../../format';
import { BigBoom, Burst, Controls, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type ActionSpec, type TableProps } from './Frame';

/** A mine going off: the tile arms, then blows. */
const ARM_MS = 450;
const BOOM_MS = 900;
/** A dud: the tile arms and holds (the loss), then the starburst, then it fizzles safe. */
const DUD_HOLD_MS = 900;
const DUD_BANNER_MS = 1200;

function Gem() {
  return (
    <svg viewBox="0 0 40 40" className="gem" aria-hidden="true">
      <polygon points="20,4 34,15 20,36 6,15" className="gem-body" />
      <polygon points="20,4 27,15 20,36 13,15" className="gem-face" />
      <line x1="6" y1="15" x2="34" y2="15" className="gem-line" />
    </svg>
  );
}

function Mine({ lit }: { lit?: boolean }) {
  return (
    <svg viewBox="0 0 40 40" className={`mine${lit ? ' lit' : ''}`} aria-hidden="true">
      <g className="mine-spikes">
        <line x1="20" y1="4" x2="20" y2="36" />
        <line x1="4" y1="20" x2="36" y2="20" />
        <line x1="9" y1="9" x2="31" y2="31" />
        <line x1="31" y1="9" x2="9" y2="31" />
      </g>
      <circle cx="20" cy="20" r="10" className="mine-body" />
      <circle cx="16" cy="16" r="3" className="mine-shine" />
    </svg>
  );
}

export function MinesTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.mines?.state as MinesState | undefined;
  const anim = snapshot.anim;
  const frames = anim?.frames ?? [];
  const dud = frames.some((f) => f.includes('A dud'));
  const boom = frames.some((f) => f.includes('B O O M'));
  const total = dud ? ARM_MS + DUD_HOLD_MS + DUD_BANNER_MS : boom ? ARM_MS + BOOM_MS : 300;
  const elapsed = useAnimClock(snapshot, actions, total);
  const [burst, fading, setBurst] = useBurst();
  // A dud: the mine arms and holds first, then luck steps in.
  useLuckMoment(snapshot, elapsed, dud ? ARM_MS + DUD_HOLD_MS : null, () => ({ what: 'That mine was a dud.', saved: true }), setBurst);

  const phase = state?.phase ?? 'idle';
  // The board in play uses its own mines; between boards (idle or done) the
  // picker and preview follow the count chosen for the next one (a Codex
  // review found a finished board kept showing its old count).
  const count = state && phase === 'playing' ? state.mineCells.length : MINE_COUNTS[state?.countIndex ?? 1] ?? 3;
  const safe = state?.revealed.filter(Boolean).length ?? 0;
  const edge = state?.edge ?? EDGE;
  const pay = (n: number) => (state ? Math.round(state.bet * minesMultiplier(count, n, edge)) : 0);

  // The tile a dud or a mine just went off on: the last one tapped. The
  // engine keeps the cursor on it for a mine, and moves it on after a dud,
  // so a dud's tile is the one revealed most recently: work it out from the
  // frame instead ("!" marks it on the lens board).
  const markedCell = (() => {
    const f = frames.find((x) => x.includes('!'));
    if (!f) return -1;
    const cells = f.split('\n').slice(0, SIZE).flatMap((row) => row.trim().split(/\s+/).map((c) => c.replace(/[[\]]/g, '')));
    return cells.indexOf('!');
  })();

  const tile = (i: number) => {
    if (!state || phase === 'idle') return <button key={i} className="tile" disabled aria-label="Tile" />;
    const isMine = state.mineCells.includes(i);
    const open = state.revealed[i];
    const hit = state.boom === i;
    if (anim && i === markedCell) {
      if (dud) {
        const fizzled = elapsed >= ARM_MS + DUD_HOLD_MS + DUD_BANNER_MS * 0.4;
        return <div key={i} className={`tile ${fizzled ? 'open dud' : 'armed'}`}>{fizzled ? <Gem /> : <Mine lit />}</div>;
      }
      if (boom) return <div key={i} className={`tile ${elapsed >= ARM_MS ? 'boom' : 'armed'}`}><Mine lit /></div>;
    }
    if (phase === 'done') {
      if (hit) return <div key={i} className="tile boom"><Mine lit /></div>;
      // The rest of the mines show once the blast has gone off, not before.
      const minesOut = !anim || elapsed >= ARM_MS + 400;
      if (isMine && minesOut) return <div key={i} className="tile mine-shown"><Mine /></div>;
      if (isMine) return <div key={i} className="tile"><span className="tile-q">?</span></div>;
      return <div key={i} className={`tile${open ? ' open' : ' unopened'}`}>{open ? <Gem /> : null}</div>;
    }
    if (open) return <div key={i} className="tile open"><Gem /></div>;
    return (
      <button key={i} className="tile" disabled={!!anim} onClick={() => pickTile(i)} aria-label={`Tile ${Math.floor(i / SIZE) + 1}-${(i % SIZE) + 1}`}>
        <span className="tile-q">?</span>
      </button>
    );
  };

  // Reveal a tile: walk the engine's cursor to it, then tap (the engine's
  // Mines runs on the lens gestures).
  const pickTile = (i: number) => {
    if (!state || state.phase !== 'playing') return;
    const steps = (i - state.cursor + CELLS) % CELLS;
    for (let k = 0; k < steps; k++) actions.move('down');
    actions.tap();
  };

  // Mine count: the engine cycles 1 / 3 / 5 / 8.
  const setCount = (n: number) => {
    if (!state || phase === 'playing') return;
    const steps = (MINE_COUNTS.indexOf(n) - (state.countIndex ?? 1) + MINE_COUNTS.length) % MINE_COUNTS.length;
    for (let k = 0; k < steps; k++) actions.tableOption('count');
  };

  const extra: ActionSpec[] = [];
  if (phase === 'playing' && !anim) {
    extra.push({ id: 'cash', label: safe === 0 ? 'Take bet back' : `Cash out ${money(pay(safe))}`, primary: true, onClick: actions.back });
  }

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (phase === 'done' && state && !anim) {
    const net = state.returned - state.bet;
    result = state.boom !== null ? `Boom. −${money(state.bet)} after ${safe} safe ${safe === 1 ? 'tile' : 'tiles'}` : net > 0 ? `${state.dud ? '★ ' : ''}Cashed out +${money(net)}` : 'Took your bet back.';
    tone = state.boom !== null ? 'loss' : net > 0 ? 'win' : '';
  }

  return (
    <>
      {/* The mine going off fills the screen (user, 2026-09-27). */}
      <BigBoom trigger={boom && anim && elapsed >= ARM_MS ? anim.frames : null} />
      <Stage className="mines-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="mines-info">
          {phase === 'playing' ? (
            <>
              <span>{count} {count === 1 ? 'mine' : 'mines'} · {safe} safe</span>
              <span>Next tile: {minesMultiplier(count, safe + 1, edge).toFixed(2)}× ({money(pay(safe + 1))})</span>
            </>
          ) : (
            <div className="mine-count" role="radiogroup" aria-label="Mines on the board">
              {MINE_COUNTS.map((n) => (
                <button key={n} role="radio" aria-checked={count === n} className={`pick${count === n ? ' on' : ''}`} onClick={() => setCount(n)} disabled={!!anim}>
                  {n}
                </button>
              ))}
              <span className="mine-count-label">{count === 1 ? 'mine' : 'mines'} · first tile {minesMultiplier(count, 1, EDGE).toFixed(2)}×</span>
            </div>
          )}
        </div>
        <div className="mine-grid">{Array.from({ length: CELLS }, (_, i) => tile(i))}</div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={phase === 'playing' ? ['count', 'start'] : ['count']} extra={extra} onAct={() => setBurst(null)} />
    </>
  );
}
